import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HelpTip } from "@/components/HelpTip";
import { Upload, Loader2, CheckCircle2, Truck } from "lucide-react";

const FIELD_LABELS = {
  name: "Product name",
  sku: "SKU / item #",
  cost: "Your cost (what you pay)",
  msrp: "MSRP / retail price",
  description: "Description",
  image_urls: "Photo URLs",
  category: "Category",
  brand: "Brand",
};

export default function AdminSupplierImport() {
  const fileRef = useRef(null);
  const [mode, setMode] = useState("csv");
  const [pasteText, setPasteText] = useState("");
  const [pasteRows, setPasteRows] = useState(null);
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState({
    supplier_name: "",
    markup_mode: "msrp",
    markup_pct: "10",
    lead_time_days: "5",
    category_default: "",
    publish: true,
    prices_only: false,
    sync_only: false,
  });

  const analyze = async (f) => {
    setFile(f);
    setResult(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/products/admin/supplier-import/analyze", fd);
      setAnalysis(data);
      setMapping(data.mapping);
      toast.success(`Read ${data.total_rows} products — AI mapped the columns, review below`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't read that file");
      setAnalysis(null);
    } finally {
      setBusy(false);
    }
  };

  const previewPrice = (row) => {
    const pct = parseFloat(settings.markup_pct) || 0;
    const base = settings.markup_mode === "msrp" && row.msrp ? row.msrp : row.cost;
    return base ? Math.ceil(base * (1 + pct / 100)) : null;
  };

  const analyzePaste = async () => {
    if (pasteText.trim().length < 20) return toast.error("Paste the supplier's listing text first");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/products/admin/supplier-import/paste-analyze", { text: pasteText });
      setPasteRows(data.rows);
      toast.success(`AI found ${data.total} products in that text`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't parse that text");
    } finally {
      setBusy(false);
    }
  };

  const importPaste = async () => {
    if (!settings.supplier_name.trim()) return toast.error("Enter the supplier's name first");
    setBusy(true);
    try {
      const { data } = await api.post("/products/admin/supplier-import/paste-commit", {
        rows: pasteRows,
        supplier_name: settings.supplier_name,
        markup_mode: settings.markup_mode,
        markup_pct: parseFloat(settings.markup_pct) || 0,
        lead_time_days: parseInt(settings.lead_time_days) || 10,
        category_default: settings.category_default || "General Merchandise",
        sync_only: settings.sync_only,
      });
      setResult(data);
      if (settings.sync_only) {
        toast.success(`Price sync done — ${data.updated} products updated${data.unmatched ? `, ${data.unmatched} not found` : ""}`);
      } else {
        toast.success(`Saved ${data.created + data.updated} hidden drafts — add photos to publish`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!settings.supplier_name.trim()) return toast.error("Enter the supplier's name first");
    if (!mapping.name) return toast.error("Map the Product name column first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("supplier_name", settings.supplier_name);
      fd.append("markup_mode", settings.markup_mode);
      fd.append("markup_pct", settings.markup_pct || "0");
      fd.append("lead_time_days", settings.lead_time_days || "5");
      fd.append("category_default", settings.category_default);
      fd.append("publish", settings.publish);
      fd.append("update_mode", settings.prices_only ? "prices_only" : "full");
      const { data } = await api.post("/products/admin/supplier-import/commit", fd);
      setResult(data);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-supplier-import">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tighter text-[#0A1C40]">Supplier Import</h1>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">
          Drop ANY supplier's CSV price list. AI reads their column names, you set your markup, and every
          item lands as a dropship Special Order — with the supplier price saved privately as your cost.
        </p>
      </div>

      {/* Mode tabs */}
      <div className="flex gap-2">
        {[["csv", "Upload CSV file"], ["paste", "Paste listing text"]].map(([m, label]) => (
          <button key={m} onClick={() => setMode(m)}
            className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${
              mode === m ? "bg-[#0A1C40] text-white border-[#0A1C40]" : "border-gray-300 text-gray-700 hover:border-[#0A1C40]"
            }`}
            data-testid={`import-mode-${m}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Step 1 — upload */}
      {mode === "csv" && (
      <div className="border-2 border-dashed border-gray-300 p-8 text-center bg-white">
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="supplier-file-input"
          onChange={(e) => e.target.files[0] && analyze(e.target.files[0])} />
        <button onClick={() => fileRef.current?.click()} disabled={busy}
          className="btn-primary inline-flex items-center gap-2" data-testid="supplier-upload-btn">
          {busy && !analysis ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {file ? `Replace file (${file.name})` : "Upload supplier CSV"}
        </button>
        <p className="text-xs text-gray-500 mt-2">First row must be column headers. Excel? Save As → CSV first.</p>
      </div>
      )}

      {mode === "paste" && (
        <div className="bg-white border border-gray-200 p-5 space-y-3">
          <p className="text-xs text-gray-600">
            Copy/paste a supplier's page (Alibaba listings, a price email, a WhatsApp catalog) — AI pulls out
            the products and prices. Price ranges use the <b>higher</b> number as your cost. No photos in pasted
            text, so everything saves as a <b>hidden draft</b> until you add pictures.
          </p>
          <textarea
            className="input-base w-full h-48 font-mono text-xs"
            placeholder="Paste the supplier's product listing text here…"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            data-testid="paste-textarea"
          />
          <button onClick={analyzePaste} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="paste-analyze-btn">
            {busy && !pasteRows ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Read products with AI
          </button>

          {pasteRows && (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-2 border-t border-gray-100">
                <div>
                  <label className="ipv-eyebrow block mb-1">Supplier name</label>
                  <input className="input-base" placeholder="e.g. Alibaba Adult" value={settings.supplier_name}
                    onChange={(e) => setSettings({ ...settings, supplier_name: e.target.value })} data-testid="paste-supplier-name" />
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1">Markup on</label>
                  <select className="input-base" value={settings.markup_mode}
                    onChange={(e) => setSettings({ ...settings, markup_mode: e.target.value })}>
                    <option value="cost">My cost</option>
                    <option value="msrp">MSRP (when present)</option>
                  </select>
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1">Markup %</label>
                  <input type="number" className="input-base" value={settings.markup_pct}
                    onChange={(e) => setSettings({ ...settings, markup_pct: e.target.value })} data-testid="paste-markup-pct" />
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1">Ships in (days)</label>
                  <input type="number" className="input-base" value={settings.lead_time_days}
                    onChange={(e) => setSettings({ ...settings, lead_time_days: e.target.value })} />
                </div>
                <div>
                  <label className="ipv-eyebrow block mb-1">Category</label>
                  <input className="input-base" placeholder="e.g. Adult (18+)" value={settings.category_default}
                    onChange={(e) => setSettings({ ...settings, category_default: e.target.value })} data-testid="paste-category" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]" data-testid="paste-preview-table">
                  <thead className="ipv-eyebrow text-left border-b-2 border-[#0A1C40]">
                    <tr><th className="p-2">Product</th><th className="p-2">Your cost</th><th className="p-2">MSRP</th><th className="p-2">You sell at</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pasteRows.slice(0, 8).map((r, i) => (
                      <tr key={i}>
                        <td className="p-2 font-semibold">{r.name}</td>
                        <td className="p-2">${r.cost}</td>
                        <td className="p-2">{r.msrp ? `$${r.msrp}` : "—"}</td>
                        <td className="p-2 font-black text-[#0A1C40]">${previewPrice({ cost: r.cost, msrp: r.msrp }) || "?"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pasteRows.length > 8 && <p className="text-xs text-gray-500 mt-1">…and {pasteRows.length - 8} more</p>}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={settings.sync_only} data-testid="paste-sync-only-checkbox"
                  onChange={(e) => setSettings({ ...settings, sync_only: e.target.checked })} />
                Price sync only — update cost &amp; prices on my existing items from this supplier, don't create anything new{" "}
                <HelpTip testId="help-paste-sync-only" text="Monthly re-paste workflow: paste the supplier's NEWEST listing, tick this, and existing products (matched by name) get fresh costs and prices in one click. Names, photos and descriptions you edited stay untouched. Items not already in your catalog are skipped." />
              </label>
              <button onClick={importPaste} disabled={busy} className="btn-gold inline-flex items-center gap-2" data-testid="paste-import-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                {settings.sync_only ? `Sync prices on ${pasteRows.length} items` : `Save ${pasteRows.length} hidden drafts`}
              </button>
            </>
          )}
        </div>
      )}

      {mode === "csv" && analysis && (
        <>
          {/* Step 2 — mapping */}
          <div className="bg-white border border-gray-200 p-5">
            <h2 className="font-bold text-[#0A1C40] mb-1">
              2. Check the column match{" "}
              <HelpTip testId="help-mapping" text="AI guessed which of THEIR columns holds each piece of info. Fix any dropdown that looks wrong — 'Your cost' must be the wholesale price YOU pay, not the retail price." />
            </h2>
            <p className="text-xs text-gray-500 mb-3">{analysis.total_rows} products found · {analysis.with_images} have photo links</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.keys(FIELD_LABELS).map((f) => (
                <div key={f}>
                  <label className="ipv-eyebrow block mb-1">{FIELD_LABELS[f]}</label>
                  <select className="input-base text-sm" value={mapping[f] || ""} data-testid={`map-${f}`}
                    onChange={(e) => setMapping({ ...mapping, [f]: e.target.value || null })}>
                    <option value="">— not in file —</option>
                    {analysis.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Step 3 — pricing settings */}
          <div className="bg-white border border-gray-200 p-5">
            <h2 className="font-bold text-[#0A1C40] mb-3">3. Your pricing & supplier</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="ipv-eyebrow block mb-1">Supplier name</label>
                <input className="input-base" placeholder="e.g. Petra" value={settings.supplier_name}
                  onChange={(e) => setSettings({ ...settings, supplier_name: e.target.value })} data-testid="supplier-name-input" />
              </div>
              <div>
                <label className="ipv-eyebrow block mb-1">Markup on{" "}
                  <HelpTip testId="help-markup-mode" text="Over MSRP: sell above the suggested retail (like the Qlife bikes). Over my cost: bigger margins — e.g. cost $50 + 30% = you sell at $65. If a row has no MSRP we fall back to cost." /></label>
                <select className="input-base" value={settings.markup_mode} data-testid="markup-mode-select"
                  onChange={(e) => setSettings({ ...settings, markup_mode: e.target.value })}>
                  <option value="msrp">MSRP / retail price</option>
                  <option value="cost">My cost</option>
                </select>
              </div>
              <div>
                <label className="ipv-eyebrow block mb-1">Markup %</label>
                <input type="number" className="input-base" value={settings.markup_pct} data-testid="markup-pct-input"
                  onChange={(e) => setSettings({ ...settings, markup_pct: e.target.value })} />
              </div>
              <div>
                <label className="ipv-eyebrow block mb-1">Ships in (days)</label>
                <input type="number" className="input-base" value={settings.lead_time_days} data-testid="lead-time-input"
                  onChange={(e) => setSettings({ ...settings, lead_time_days: e.target.value })} />
              </div>
              <div>
                <label className="ipv-eyebrow block mb-1">Fallback category</label>
                <input className="input-base" placeholder="General Merchandise" value={settings.category_default}
                  onChange={(e) => setSettings({ ...settings, category_default: e.target.value })} data-testid="category-default-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm mt-3">
              <input type="checkbox" checked={settings.publish} data-testid="publish-checkbox"
                onChange={(e) => setSettings({ ...settings, publish: e.target.checked })} />
              Publish immediately (items without photos are saved hidden either way)
            </label>
            <label className="flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" checked={settings.prices_only} data-testid="prices-only-checkbox"
                onChange={(e) => setSettings({ ...settings, prices_only: e.target.checked })} />
              Price sync only — update cost &amp; prices on existing items, don't touch names, photos or
              descriptions I've edited{" "}
              <HelpTip testId="help-prices-only" text="Use this when re-uploading a supplier's NEWEST price list each month: existing products keep your custom names/photos/descriptions and only get fresh costs and prices. Brand-new items in the file are still added in full." />
            </label>
          </div>

          {/* Preview */}
          <div className="bg-white border border-gray-200 p-5 overflow-x-auto">
            <h2 className="font-bold text-[#0A1C40] mb-3">Preview (first {analysis.preview.length})</h2>
            <table className="w-full text-sm min-w-[640px]" data-testid="supplier-preview-table">
              <thead className="ipv-eyebrow text-left border-b-2 border-[#0A1C40]">
                <tr><th className="p-2">Product</th><th className="p-2">SKU</th><th className="p-2">Your cost</th><th className="p-2">MSRP</th><th className="p-2">You sell at</th><th className="p-2">Photo</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {analysis.preview.map((r, i) => (
                  <tr key={i}>
                    <td className="p-2 font-semibold">{r.name || <span className="text-red-600">missing</span>}</td>
                    <td className="p-2 text-gray-500">{r.sku || "auto"}</td>
                    <td className="p-2">{r.cost ? `$${r.cost}` : "—"}</td>
                    <td className="p-2">{r.msrp ? `$${r.msrp}` : "—"}</td>
                    <td className="p-2 font-black text-[#0A1C40]">{previewPrice(r) ? `$${previewPrice(r)}` : <span className="text-red-600">no price</span>}</td>
                    <td className="p-2">{r.images.length ? <CheckCircle2 className="w-4 h-4 text-green-700" /> : <span className="text-amber-600 text-xs font-bold">none</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={doImport} disabled={busy} className="btn-primary mt-4 inline-flex items-center gap-2" data-testid="supplier-import-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
              Import {analysis.total_rows} products
            </button>
          </div>
        </>
      )}

      {result && (
        <div className="bg-green-50 border-2 border-green-700 p-5" data-testid="supplier-import-result">
          <h2 className="font-black text-green-800 text-lg">Import complete</h2>
          <p className="text-sm mt-1">
            <b>{result.created}</b> created · <b>{result.updated}</b> updated · <b>{result.skipped}</b> skipped
            {result.unmatched > 0 && <> · <b>{result.unmatched}</b> not in your catalog (skipped)</>}
            {result.hidden_no_photo > 0 && <> · <b>{result.hidden_no_photo}</b> saved hidden (no photo yet)</>}
            <span className="text-gray-600"> — SKUs prefixed {result.supplier_prefix}-</span>
          </p>
          {result.changes?.length > 0 && (
            <div className="mt-3 overflow-x-auto" data-testid="price-sync-changes">
              <div className="font-bold text-green-900 text-sm mb-1">Price changes ({result.changes.length})</div>
              <table className="w-full text-xs min-w-[520px] bg-white border border-green-200">
                <thead className="text-left border-b border-green-200">
                  <tr><th className="p-2">Product</th><th className="p-2">SKU</th><th className="p-2">Price</th><th className="p-2">Your cost</th></tr>
                </thead>
                <tbody className="divide-y divide-green-100">
                  {result.changes.map((c, i) => (
                    <tr key={i}>
                      <td className="p-2 font-semibold">{c.name}</td>
                      <td className="p-2 text-gray-500">{c.sku}</td>
                      <td className="p-2">
                        <span className="text-gray-400 line-through">${c.old_price ?? "—"}</span>{" "}
                        <b className="text-[#0A1C40]">${c.new_price}</b>
                      </td>
                      <td className="p-2">
                        <span className="text-gray-400 line-through">${c.old_cost ?? "—"}</span>{" "}
                        <b>${c.new_cost}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.errors?.length > 0 && (
            <ul className="text-xs text-amber-800 mt-2 list-disc pl-4">
              {result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
